import React, { useEffect, useMemo, useState } from "react";

import { InfoCircleOutlined } from "@ant-design/icons";
import { pdf } from "@react-pdf/renderer";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import PdfPreviewModal from "../../../../Common/UIComponents/PdfPreviewModal";
import SwabListPDF from "./SwabListPDF";
import { buildExportHeaders, buildExportRow } from "./swabListExportUtils";

import "./SwabListExport.css";

export default function SwabListExport({
  open,
  onClose,
  swabList = [],
  tableHeaders = [],
  testsColumnLabel = "Tests Out of Spec",
  finalResultColumnLabel = "",
  modalTitle = "Sample List",
  dateRange = "",
  includeReceivedDate = true,
}) {
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFileName, setPdfFileName] = useState("");
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const headers = buildExportHeaders({
    tableHeaders,
    testsColumnLabel,
    finalResultColumnLabel,
    includeReceivedDate,
  });

  const getXlsxFileName = () => {
    const fileName = `${modalTitle}_${Date.now()}.xlsx`;
    return fileName.replace(/\s|\//g, "_");
  };

  useEffect(() => {
    if (!open) {
      setPdfBlob(null);
      setPdfFileName("");
      setGeneratingPDF(false);
      return undefined;
    }

    let cancelled = false;
    setGeneratingPDF(true);
    setPdfBlob(null);

    const generatePdf = async () => {
      const fileName = `${modalTitle}_${Date.now()}.pdf`.replace(/\s|\//g, "_");
      try {
        const blob = await pdf(SwabListPDF({
          swabList,
          tableHeaders,
          testsColumnLabel,
          finalResultColumnLabel,
          modalTitle,
          dateRange,
          includeReceivedDate,
          getFileName: () => fileName,
        })).toBlob();

        if (cancelled) return;

        if (!blob || blob.size === 0) {
          toast.error("Failed to generate pdf");
          setPdfBlob(null);
          return;
        }

        setPdfFileName(fileName);
        setPdfBlob(blob);
      } catch (error) {
        if (cancelled) return;
        toast.error("Failed to generate pdf");
        setPdfBlob(null);
      } finally {
        if (!cancelled) {
          setGeneratingPDF(false);
        }
      }
    };

    generatePdf();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    swabList,
    tableHeaders,
    testsColumnLabel,
    finalResultColumnLabel,
    modalTitle,
    dateRange,
    includeReceivedDate,
  ]);

  const previewFiles = useMemo(() => {
    if (!pdfBlob) return [];

    return [{
      key: pdfFileName || modalTitle,
      fileName: pdfFileName || `${modalTitle}.pdf`,
      src: pdfBlob,
    }];
  }, [modalTitle, pdfBlob, pdfFileName]);

  const handleDownloadExcel = () => {
    try {
      const worksheetData = swabList.map((row) => buildExportRow(row, {
        tableHeaders,
        includeReceivedDate,
        finalResultColumnLabel,
      }));
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...worksheetData]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Swab List");
      XLSX.writeFile(workbook, getXlsxFileName());
    } catch (e) {
      toast.error("Failed to generate excel file");
    }
  };

  return (
    <PdfPreviewModal
      open={open}
      title={(
        <span className="SwabListExport__Info">
          <InfoCircleOutlined className="SwabListExport__InfoIcon" aria-hidden />
          <span>
            Preview your sample list below, then print a copy or download it as an Excel file for your records.
          </span>
        </span>
      )}
      onClose={onClose}
      files={previewFiles}
      loading={generatingPDF}
      onDownload={handleDownloadExcel}
      downloadDisabled={swabList.length === 0}
      errorMessage="Unable to preview this sample list."
      width="40vw"
      zIndex={1100}
    />
  );
}
